import ResaleListingForm from "./form";

import { ListingType, PriceRoles } from "@/modules/property-tracks/dtos";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { useEditListing } from "@/modules/property-tracks/hooks/listings/edit-listing.hook";
import {
  ResaleListingFormFields,
  ResaleListingSubmitFormFields,
  clearedCountToNull,
} from "@/modules/property-tracks/utils";

export default function EditResaleListing({
  open,
  setOpen,
  values,
  listingUid,
  trackUid,
  defaultStep,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  values: ResaleListingFormFields;
  listingUid: string;
  trackUid: string;
  defaultStep?: number;
}) {
  const mutation = useEditListing();

  const handleSubmit = (body: ResaleListingSubmitFormFields) => {
    const mergedDoucments = [
      ...(body.resaleDocumentsDetails?.floorPlanUids ?? []),
      ...(body.resaleDocumentsDetails?.mediaUids ?? []),
    ];
    mutation.mutate(
      {
        uid: listingUid,
        data: {
          listingType: ListingType.RESALE,
          publish: body.publish ?? false,
          documentUids:
            mergedDoucments.length > 0 ? mergedDoucments : undefined,
          propertyPrice: {
            priceRole: PriceRoles.LISTING,
            paymentType: body.propertyPriceDetails?.paymentType,
            ...(body.propertyPriceDetails?.paymentType === "cash"
              ? {
                  price: body.propertyPriceDetails?.price,
                }
              : {
                  price:
                    (Number(body.propertyPriceDetails?.downPayment) || 0) +
                    (Number(body.propertyPriceDetails?.remainingInstallments) ||
                      0),
                  downPayment: body.propertyPriceDetails?.downPayment,
                  remainingInstallments:
                    body.propertyPriceDetails?.remainingInstallments,
                  maintenance: body.propertyPriceDetails?.maintenance,
                  clubhouse: body.propertyPriceDetails?.clubhouse,
                  parking: body.propertyPriceDetails?.parking,
                  durationRemainingMonths:
                    body.propertyPriceDetails?.durationRemainingMonths,
                  durationRemainingYears:
                    body.propertyPriceDetails?.durationRemainingYears,
                  installmentFrequency:
                    body.propertyPriceDetails?.installmentFrequency?.value,
                }),
          },
          property: {
            compoundId: body.propertyDetails?.compound.value,
            propertyTypeId: body.propertyDetails?.propertyType.value,
            unitId: body.propertyDetails?.unitId,
            // The multi-step submit passes raw form values, so a cleared input arrives as "" and
            // the backend coerces it to 0. Null is what clears the column.
            numberOfBedrooms: clearedCountToNull(
              body.propertyDetails?.numberOfBedrooms
            ),
            numberOfBathrooms: clearedCountToNull(
              body.propertyDetails?.numberOfBathrooms
            ),
            unitArea: clearedCountToNull(body.propertyDetails?.unitArea),
            phase: body.propertyDetails?.phase,
            landArea: clearedCountToNull(body.propertyDetails?.landArea),
            gardenArea: clearedCountToNull(body.propertyDetails?.gardenArea),
            roofArea: clearedCountToNull(body.propertyDetails?.roofArea),
            floorNumber: clearedCountToNull(body.propertyDetails?.floorNumber),
            numberOfFloors: clearedCountToNull(body.propertyDetails?.numberOfFloors),
            deliveryDate: body.propertyDetails?.readyToMove
              ? undefined
              : body.propertyDetails?.deliveryDate,
            lastInstallmentDate: body.propertyDetails?.lastInstallmentDate,
            readyToMove: body.propertyDetails?.readyToMove,
            description: body.propertyDetails?.description ?? undefined,
            finishing: body.propertyDetails?.finishing?.value,
            websiteListingId: clearedCountToNull(
              body.propertyDetails?.websiteListingId
            ),
            amenityIds: [...(body.propertyDetails?.amenityIds?.values() ?? [])],
          },
          descriptionAr:
            body.listingDescriptionDetails?.descriptionAr ?? undefined,
          descriptionEn:
            body.listingDescriptionDetails?.descriptionEn ?? undefined,
        },
      },
      {
        onSuccess: () => {
          showToast({
            type: ToastType.SUCCESS,
            message: "Listing updated successfully",
            toastId: TOAST_IDS.EDIT_LISTING_SUCCESS,
          });
          setOpen(false);
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_LISTING_ERROR,
          });
        },
      },
    );
  };

  return (
    <>
      <ResaleListingForm
        open={open}
        setOpen={setOpen}
        header={"Edit Listing"}
        isSubmitting={mutation.isPending}
        onSubmit={handleSubmit}
        trackUid={trackUid}
        defaultValues={values}
        defaultStep={defaultStep}
      />
    </>
  );
}
